// @ts-check
import { throttle } from '../common';
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, Thumbs, A11y, FreeMode, Keyboard, Mousewheel, Zoom } from 'swiper/modules';
import { common as twConfig } from '../../../../tailwind/common.config';
import { showSelectedVariant, getVariantIndex } from './product-detail-page';
Swiper.use([Navigation, Pagination, Autoplay, Thumbs, A11y, FreeMode, Keyboard, Mousewheel, Zoom]);
export const selectors = Object.freeze({
  pdp_media_parent: '.product__media',
  parent: '[data-items-element="swiper__parent"]',
  carousel: '[data-items-element="swiper__viewport"]',
  wrapper: '[data-items-element="swiper-wrapper"]',
  slide: '[data-items-element="swiper-slide"]',
  next: '.swiper__navigation--next',
  prev: '.swiper__navigation--prev',
  pagination: '.swiper__pagination',
  counter: '.swiper-pagination',
  activeCounter: '.swiper-pagination-bullet-active',
  thumbnails: {
    wrapper: '[data-items-element="swiper-wrapper--thumbs"]',
    parent: '.swiper--thumbs',
    carousel: '.swiper-viewport--thumbs',
    thumbs: '.swiper--thumbs .swiper-thumb'
  },
  activeThumbnail: '.swiper-slide-thumb-active'
});

/** initialize all carousels */
export function carouselInit() {
  /** @type {NodeListOf<HTMLElement>} */
  const parents = document.querySelectorAll(selectors.parent);
  parents.forEach(parent => {
    initSingleCarousel(parent);
  });
}
const initSingleCarousel = parent => {
  const carousel = parent.querySelector(selectors.carousel);
  if (!(carousel instanceof HTMLElement) || carousel.swiper) return;
  /** @type {HTMLElement | null} */
  const paginationElement = parent.querySelector(selectors.pagination);
  const prevEl = parent.querySelector('.swiper__navigation--prev');
  const nextEl = parent.querySelector('.swiper__navigation--next');
  const hasNav = prevEl instanceof HTMLElement && nextEl instanceof HTMLElement;
  const autoplayDelay = parseFloat(parent.dataset.carouselAutoplay ?? '0') * 1000;
  const spaceBetweenX = getSpaceBetween(parent);
  const slidesPerView = getSlidesPerView(parent);
  const slidesPerGroup = getSlidesPerGroup(parent);
  const responsiveLayout = getResponsiveLayout(parent);
  const peek = getPeek(parent);
  const carouselType = getCarouselType(carousel);
  /** @type {NodeListOf<HTMLElement>} */
  const wrappers = parent.querySelectorAll(selectors.wrapper);
  /** @type {NodeListOf<HTMLElement>} */
  const slides = parent.querySelectorAll(selectors.slide);
  const slideCount = slides.length;
  const paginationAriaLabel = paginationElement?.dataset.label;
  const breakpoint = getBreakpointString(responsiveLayout, breakpoints);
  const mediaQuery = window.matchMedia(breakpoint);
  const initialVariantIndex = getVariantIndex();
  const zoom = parent.dataset.carouselZoom === 'true';
  const paginationType = parent.dataset.carouselPaginationType;

  // TODO: .swiper-wrapper is added in setCarouselClasses function now,
  // so we might not need to add .swiper-wrapper to any carousels
  wrappers.forEach(wrapper => {
    //  Don't add swiper-wrapper class to pdp-media if its not a carousel on tablet. Since it applies the flex class,
    //  which causes image items to be hidden on desktop when layout is an image list or tiered list
    showHiddenCarouselUI(parent);
    if (getCarouselType(wrapper) === 'pdp-media' && parent.dataset.carouselLayoutMd !== 'carousel') return;
    wrapper.classList.add('swiper-wrapper');
  });
  slides.forEach(slide => slide.classList.add('swiper-slide'));

  // Prevent duplicate swiper aria-live regions
  const notifications = parent.querySelectorAll('.swiper-notification');
  if (notifications) notifications.forEach(notification => notification.remove());

  // Exit if there are no active breakpoints
  if (breakpoint.length <= 0) return;
  const swiperThumbs = getThumbs(carousel);
  const slideActiveCounter = parent.querySelector(selectors.activeCounter);
  const slideCounter = parent.querySelector(selectors.counter);
  if (slideActiveCounter) slideActiveCounter.textContent = `1`;
  if (slideCounter) slideCounter.textContent = `${slideCount}`;
  const swiperOptions = {
    slidesPerView: slidesPerView.sm + (peek.sm.enabled ? peek.sm.value : 0),
    slidesPerGroup: slidesPerGroup.sm,
    initialSlide: initialVariantIndex,
    navigation: {
      enabled: hasNav,
      nextEl: nextEl,
      prevEl: prevEl
    },
    pagination: {
      type: paginationType,
      el: paginationElement,
      clickable: true,
      renderBullet: function (index, className) {
        const bulletIndex = carouselType === 'hero-banner-doubled' ? index % (slides.length / 2) : index;
        return `<button class="${className}" aria-label="${paginationAriaLabel?.replace('{{ index }}', (bulletIndex + 1).toString())}"></button>`;
      }
    },
    createElements: true,
    resizeObserver: true,
    spaceBetween: spaceBetweenX,
    thumbs: {
      swiper: swiperThumbs
    },
    autoplay: autoplayDelay > 0 ? {
      delay: autoplayDelay
    } : undefined,
    loop: parent.dataset.carouselLoop === 'true' && slideCount > 2,
    centeredSlides: parent.dataset.carouselAlign === 'center',
    freeMode: parent.dataset.carouselDragfree === 'true',
    zoom: zoom,
    keyboard: {
      enabled: true,
      onlyInViewport: true
    },
    mousewheel: {
      forceToAxis: true
    },
    a11y: {
      enabled: true,
      // @ts-expect-error accessibilityStrings is a global
      nextSlideMessage: window.accessibilityStrings.nextSlide,
      // @ts-expect-error accessibilityStrings is a global
      prevSlideMessage: window.accessibilityStrings.prevSlide
    },
    breakpoints: {
      [breakpoints.md]: {
        slidesPerView: slidesPerView.md + (peek.md.enabled ? peek.md.value : 0),
        slidesPerGroup: slidesPerGroup.md,
        centeredSlides: parent.dataset.carouselAlignMd === 'center'
      },
      [breakpoints.lg]: {
        slidesPerView: slidesPerView.lg + (peek.md.enabled ? peek.md.value : 0),
        slidesPerGroup: slidesPerGroup.lg,
        centeredSlides: parent.dataset.carouselAlignMd === 'center'
      }
    },
    on: {
      afterInit: swiper => {
        handlePaginationSetup(swiper, carouselType, parent, slides);
        // Swiper intermittently fails to apply disabled state to prev and next navigation during section load event
        // To fix, explicitly set the disabled state when carousel is at the beginning or end
        if (swiper.realIndex === 0) {
          if (!(swiper.navigation.prevEl instanceof HTMLElement) || typeof swiper.navigation.prevEl === 'undefined') return;
          swiper.navigation.prevEl.setAttribute('aria-disabled', 'true');
          swiper.navigation.prevEl.setAttribute('disabled', 'true');
        }
        if (swiper.realIndex === swiper.slides.length - 1) {
          if (!(swiper.navigation.nextEl instanceof HTMLElement) || typeof swiper.navigation.nextEl === 'undefined') return;
          swiper.navigation.nextEl.setAttribute('aria-disabled', 'true');
          swiper.navigation.nextEl.setAttribute('disabled', 'true');
        }
      },
      activeIndexChange: swiper => {
        // Clicking thumbs de-syncs state
        // and causes arrows to be disabled even if isBeginning and isEnd != true
        // To fix, we reset the nav/pagination, and then remove disabled attrs from
        // navigation if the swiper is not actually at the beginning/end
        swiper.pagination.render();
        swiper.navigation.destroy();
        swiper.navigation.init();
        if (!swiper.isBeginning && !swiper.isEnd) {
          [swiper.navigation.nextEl, swiper.navigation.prevEl].forEach(el => {
            if (!(el instanceof HTMLElement) || typeof el === 'undefined') return;
            el.setAttribute('aria-disabled', 'false');
            el.removeAttribute('disabled');
          });
        }
        if (zoom) {
          swiper.zoom.out();
        }
      },
      slideChange: swiper => {
        handlePaginationSetup(swiper, carouselType, parent, slides);
      }
    }
  };
  let swiperObject = null;
  if (mediaQuery.matches) {
    swiperObject = new Swiper(carousel, {
      init: false,
      ...swiperOptions
    });
    const swiperWrapper = swiperObject.el.querySelector(selectors.wrapper);
    if (swiperWrapper) {
      const carouselType = getCarouselType(swiperWrapper);
      if (carouselType === 'grid-carousel' || carouselType === 'hero-banner' || carouselType === 'pdp-media' || carouselType === 'hero-banner-doubled') {
        setCarouselClasses(swiperWrapper);
      }
    }
    parent.dataset.carouselSlideCount = swiperObject.slides.length;
    if (zoom) {
      // TODO: Do we need to scope this to the swiper parent?
      zoomInline(swiperObject);
    }
    swiperObject.init();
  }
  mediaQuery.addEventListener('change', e => {
    if (e.matches) {
      if (!swiperObject || swiperObject.destroyed) {
        swiperObject = new Swiper(carousel, swiperOptions);
        const swiperWrapper = swiperObject.el.querySelector(selectors.wrapper);
        if (swiperWrapper) {
          const carouselType = getCarouselType(swiperWrapper);
          if (carouselType === 'grid-carousel' || carouselType === 'hero-banner' || carouselType === 'pdp-media' || carouselType === 'hero-banner-doubled') {
            setCarouselClasses(swiperWrapper);
            swiperObject.destroy(true, true);
            initSingleCarousel(parent);
          }
        }
        if (zoom) {
          zoomInline(swiperObject);
        }
      }
    } else {
      if (swiperObject && !swiperObject.destroyed) {
        const swiperWrapper = swiperObject.el.querySelector(selectors.wrapper);
        if (swiperWrapper) {
          const carouselType = getCarouselType(swiperWrapper);
          if (carouselType === 'grid-carousel' || carouselType === 'pdp-media') {
            setNonCarouselClasses(swiperWrapper);
          }
        }
        swiperObject.destroy(true, true);
      }
    }
  });
  return swiperObject;
};

/**
 * showHiddenCarouselUI
 * @description - Called after a carousel is initialized. Removes the 'hidden' class from slides index 1-N
 * @param {Element} parent - swiper parent element
 */
const showHiddenCarouselUI = parent => {
  const callback = cssLoadedInterval => {
    // Clear the interval that is set later so it no longer checks the document.styleSheets
    clearInterval(cssLoadedInterval);
    const carouselNav = parent.querySelectorAll('.carousel-nav');
    if (carouselNav) {
      carouselNav.forEach(navItem => navItem.classList.remove('invisible', 'opacity-0'));
    }
    const pdpSlides = parent.querySelectorAll('.pdp-image-wrapper');
    if (pdpSlides) {
      pdpSlides.forEach(slide => slide.classList.remove('hidden'));
    }
  };
  const stylesCss = document.getElementById('styles_css_bundle');
  // Periodically check the styleSheets loaded in the document
  const cssLoadedInterval = setInterval(() => {
    // loop through all stylesheets loaded
    Array.from(document.styleSheets).forEach(styleSheet => {
      // check if the stylesheet is the styles_css_bundle
      if (styleSheet.ownerNode === stylesCss) {
        // if so, trigger the callback and pass the interval id so it can be cleared
        callback(cssLoadedInterval);
      }
    });
  }, 500);
};

/**
 * handlePaginationSetup
 * @description - Called when a carousel is initialized and the slide changes
 * @param {Swiper} swiper - .swiper-wrapper element
 * @param {String | null} carouselType - type of carousel
 * @param {Element} parent - swiper parent element
 * @param {NodeListOf<HTMLElement>} slides - slides
 */
function handlePaginationSetup(swiper, carouselType, parent, slides) {
  /** @type {NodeListOf<HTMLElement>} */
  const bullets = parent.querySelectorAll('.swiper-pagination-bullet');
  if (carouselType === 'hero-banner-doubled') {
    const realIndex = swiper.realIndex;
    const originalSlideCount = slides.length / 2;
    bullets.forEach(el => el.classList.remove('swiper-pagination-bullet-active', 'hidden'));
    bullets.forEach(el => {
      el.style.display = '';
    });

    // Activate the 'true' bullet, and its duplicate
    const bullet1 = realIndex % originalSlideCount;
    const bullet2 = bullet1 + originalSlideCount;
    [bullet1, bullet2].forEach(i => {
      bullets[i]?.classList.add('swiper-pagination-bullet-active');
    });

    // Hide either the first half or last half of bullets
    bullets.forEach((el, i) => {
      if (realIndex < originalSlideCount) {
        // Viewing originals: hide duplicates
        if (i >= originalSlideCount) el.style.display = 'none';
      } else {
        // Viewing duplicates: hide originals
        if (i < originalSlideCount) el.style.display = 'none';
      }
    });
  }
}

/**
 * getCarouselType
 * @description - Called when a carousel is resized and is now a swiper carousel
 * @param {Element} swiperWrapperEl - .swiper-wrapper element
 * @returns {String | null} type of carousel ['hero-banner'|'grid-carousel'|'lookbook-carousel'|'pdp-media']
 */
const getCarouselType = swiperWrapperEl => {
  const parentType = swiperWrapperEl.closest('[data-carousel-type]');
  if (!parentType) return null;
  return parentType.dataset.carouselType;
};

/**
 * setCarouselClasses
 * @description - Called when a carousel is resized and is now a swiper carousel
 * @param {Element} swiperWrapperEl - .swiper-wrapper element
 */
const setCarouselClasses = swiperWrapperEl => {
  swiperWrapperEl.className = `swiper-wrapper ${swiperWrapperEl.dataset.carouselClass}`;
};

/**
 * setNonCarouselClasses
 * @description - Called when a carousel is resized and is no longer a swiper, fallback to grid settings
 * @param {Element} swiperWrapperEl - .swiper-wrapper element
 */
const setNonCarouselClasses = swiperWrapperEl => {
  swiperWrapperEl.className = `${swiperWrapperEl.dataset.class}`;
};

/**
 * getBreakpointString
 * @param {Object} responsiveLayout - Object {sm, md, lg} - where each ['left', 'center', 'carousel', 'carousel_peek']
 * @returns {String} CSS Media Query e.g. (max-width: 680px), (min-width: 681px) and (max-width: 1024px), (min-width: 2000px)
 */
const getBreakpointString = (responsiveLayout, breakpoints) => {
  const arr = [];
  if (responsiveLayout.sm === 'carousel' || responsiveLayout.sm === 'carousel_peek') {
    arr.push(`(max-width:${breakpoints.md - 1}px)`);
  }
  if (responsiveLayout.md === 'carousel' || responsiveLayout.md === 'carousel_peek') {
    arr.push(`(min-width:${breakpoints.md}px) and (max-width:${breakpoints.lg - 1}px)`);
  }
  if (responsiveLayout.lg === 'carousel' || responsiveLayout.lg === 'carousel_peek') {
    arr.push(`(min-width:${breakpoints.lg}px)`);
  }
  return arr.join(',');
};
function zoomInline(swiper) {
  document.querySelectorAll(selectors.thumbnails.thumbs)?.forEach(thumbEl => {
    thumbEl.addEventListener('click', () => {
      swiper.zoom.out();
      const index = swiper.thumbs.swiper.clickedIndex;
      swiper.slideTo(index);
    });
  });
  let zoomChanged = false;
  swiper.on('zoomChange', function (_swiper, scale) {
    if (scale > 1) {
      swiper.allowSlideNext = false;
      swiper.allowSlidePrev = false;
      zoomChanged = true;
    } else {
      swiper.allowSlideNext = true;
      swiper.allowSlidePrev = true;
      zoomChanged = false;
    }
  });
  swiper.on('navigationPrev', function () {
    if (zoomChanged) {
      swiper.zoom.out();
      const index = swiper.realIndex - 1;
      swiper.slideTo(index);
    }
  });
  swiper.on('navigationNext', function () {
    if (zoomChanged) {
      swiper.zoom.out();
      const index = swiper.realIndex + 1;
      swiper.slideTo(index);
    }
  });
}

/**
 * slideToIndex
 *  @param {Number} index - index to scroll pdp carousel to
 */
function slideToIndex(swiperChildElement, index) {
  /** @type {HTMLElement & { swiper?: Swiper } | null } */
  const swiperParent = swiperChildElement.closest('.product__media [data-items-element="swiper__parent"] .swiper-initialized');
  if (!(swiperParent instanceof HTMLElement)) return;
  const swiperObject = swiperParent.swiper;
  if (!swiperObject) return;

  // Navigate and disable animation temporarily
  const originalSpeed = swiperObject.params.speed;
  swiperObject.slideTo(index, 0);
  setTimeout(() => {
    swiperObject.params.speed = originalSpeed;
  }, 0);
}

/**
 * getPeek
 * @param {HTMLElement| undefined} parent - carousel parent element
 * @returns {Object}
 */
const getPeek = parent => {
  if (parent === undefined) {
    return {
      sm: false,
      md: false
    };
  }
  return {
    sm: {
      enabled: parent.dataset.carouselPeek === 'true',
      value: parseFloat(parent.dataset.carouselPeekValue || '0.5')
    },
    md: {
      enabled: parent.dataset.carouselPeekMd === 'true',
      value: parseFloat(parent.dataset.carouselPeekValueMd || '0.5')
    }
  };
};

/**
 * getSpaceBetween
 * @param {Element} parentEl - gap size, values should match liquid settings.grid_gap_x
 * @returns {Number | undefined}
 */

function getSpaceBetween(parentEl) {
  const rem = parseInt(getComputedStyle(parentEl).getPropertyValue('--grid-gap-x'));
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

/**
 * getThumbs
 * @param {HTMLElement} viewportElement - the carousel's viewport element
 * @returns {Swiper | undefined}
 */
function getThumbs(viewportElement) {
  // Setup thumbnail carousel (if exists)
  const parentEl = viewportElement.closest(selectors.pdp_media_parent);
  if (!(parentEl instanceof HTMLElement)) return;
  const thumbParentEl = parentEl.querySelector(selectors.thumbnails.parent);
  if (!(thumbParentEl instanceof HTMLElement)) return;
  const directionMd = thumbParentEl.dataset.directionMd === 'vertical' ? 'vertical' : 'horizontal';
  const centered = thumbParentEl.dataset.centered === 'center';
  const centeredMd = thumbParentEl.dataset.centeredMd === 'center';

  /** @type {HTMLElement | null} */
  const thumbViewportElement = thumbParentEl.querySelector(selectors.thumbnails.carousel);
  if (!thumbViewportElement) return;
  const thumbs = new Swiper(thumbViewportElement, {
    slidesPerView: 'auto',
    spaceBetween: 12,
    direction: 'horizontal',
    initialSlide: 0,
    resizeObserver: true,
    watchOverflow: true,
    watchSlidesProgress: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true
    },
    centerInsufficientSlides: centered,
    a11y: {
      enabled: true
    },
    breakpoints: {
      [breakpoints.md]: {
        direction: directionMd,
        centerInsufficientSlides: centeredMd
      }
    }
  });
  const thumbsParent = viewportElement.querySelector(selectors.wrapper);
  if (thumbsParent) {
    // Grab first item in main PDP carousel, exit of DNE
    const firstMediaItem = viewportElement.querySelector(selectors.wrapper)?.querySelector('li');
    if (!firstMediaItem) return;

    // Edit if we have issues measuring height
    const firstMediaItemHeight = firstMediaItem.getBoundingClientRect().height;
    if (!firstMediaItemHeight || firstMediaItemHeight <= 0) {
      thumbViewportElement.style.removeProperty('max-height');
      return;
    }

    // Set initial container size and event listeners
    resizeThumbnailContainer(viewportElement, thumbViewportElement);

    // Resize again once css loads and layout is done shifting
    const stylesCss = document.getElementById('styles_css_bundle');
    if (stylesCss) {
      stylesCss.addEventListener('load', () => resizeThumbnailContainer(viewportElement, thumbViewportElement));
    }

    // Resize when window resize occurs
    window.addEventListener('resize', () => {
      throttle(resizeThumbnailContainer(viewportElement, thumbViewportElement));
    }, {
      passive: true
    });
  }
  return thumbs;
}

/**
 * Update max height on PDP carousel
 * @param {HTMLElement} viewportElement - the carousel's viewport element
 * @param {HTMLElement} thumbViewportElement - the thumbnail carousel viewport
 */
const resizeThumbnailContainer = (viewportElement, thumbViewportElement) => {
  const firstItemHeight = viewportElement.querySelector(selectors.wrapper)?.querySelector('li')?.getBoundingClientRect().height;
  if (!firstItemHeight) return;
  thumbViewportElement.style.setProperty('max-height', firstItemHeight + 'px');
};

/**
 * get the number of slides per view for each breakpoint
 * @param {HTMLElement} el - the carousel's parent element
 * @returns {{ sm: number, md: number, lg: number }}
 */
function getSlidesPerView(el) {
  const sm = el.dataset.slidesPerView !== undefined ? parseFloat(el.dataset.slidesPerView) : 0;
  const md = el.dataset.slidesPerViewMd !== undefined ? parseFloat(el.dataset.slidesPerViewMd) : 0;
  const lg = el.dataset.slidesPerViewLg !== undefined ? parseFloat(el.dataset.slidesPerViewLg) : 0;
  return {
    sm,
    md,
    lg
  };
}

/**
 * get the number of slides per view for each breakpoint
 * @param {HTMLElement} el - the carousel's parent element
 * @returns {{ sm: string, md: string, lg: string }}
 */
function getResponsiveLayout(el) {
  const sm = el.dataset.carouselLayout !== undefined ? el.dataset.carouselLayout : '';
  const md = el.dataset.carouselLayoutMd !== undefined ? el.dataset.carouselLayoutMd : '';
  const lg = el.dataset.carouselLayoutMd !== undefined ? el.dataset.carouselLayoutMd : '';
  return {
    sm,
    md,
    lg
  };
}

/**
 * get the number of slides per view for each breakpoint
 * @param {HTMLElement} el - the carousel's parent element
 * @returns {{ sm: number, md: number, lg: number }}
 */
function getSlidesPerGroup(el) {
  const sm = parseInt(el.dataset.slidesPerView ?? '1', 10) || 1;
  const md = parseInt(el.dataset.slidesPerViewMd ?? '1', 10) || sm;
  const lg = parseInt(el.dataset.slidesPerViewLg ?? '1', 10) || md;
  return {
    sm,
    md,
    lg
  };
}

/** breakpoint values from tailwind config (in px) */
const breakpoints = {
  // @note: parseFloat will strip the 'px' suffix
  md: parseFloat(twConfig.theme.extend.screens.md),
  lg: parseFloat(twConfig.theme.extend.screens.lg)
};

/**
 * Only show images of selected variant
 * @param {CustomEvent} event - fired in variant-selector.js
 */
document.addEventListener('umg:variant:change', function (event) {
  if (!(event instanceof CustomEvent)) return;
  showSelectedVariant(event.detail.variantId);
});

/**
 * Navigate to variant image
 * @param {CustomEvent} event - fired in variant-selector.js
 */
document.addEventListener('umg:variant:change', function (event) {
  if (!(event instanceof CustomEvent)) return;

  // Get button from the image url
  const srcArray = event.detail.previewImageSrc.split('/');
  const src = srcArray[srcArray.length - 1];
  const imageInCarousel = document.querySelector(`.pdp-image-wrapper img[src*="${src}"]`);
  const imageInCarouselParentButton = imageInCarousel?.closest('button');

  // Get index from button
  const index = parseInt(imageInCarouselParentButton?.dataset.index || '-1');
  if (index < 0) return;
  slideToIndex(imageInCarouselParentButton, index);
});
document.addEventListener('DOMContentLoaded', function () {
  carouselInit();
});